import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiInstance from '@services/api/apiInstance'
import { Person, PersonsListResponse, PersonUpdateData, PersonBankAccount } from './contacts.models'
import { useAccessToken } from '@services/token/token.hooks'

// SECURITY: This module handles sensitive personal data (PESEL, NIP, IBAN, etc.)
// Caching is DISABLED for all contacts queries to ensure data privacy and security
const CONTACTS_QUERY_KEY = 'contacts'

export const usePersonsList = (search?: string) => {
  return useQuery({
    queryKey: [CONTACTS_QUERY_KEY, 'persons', { search }],
    queryFn: async (): Promise<PersonsListResponse> => {
      const response = await apiInstance.get<PersonsListResponse>('/admin/contacts/persons/', {
        params: search ? { search } : undefined
      })
      return response.data
    },
    enabled: true,
    // Security: Disable caching for sensitive personal data
    staleTime: 0, // Data is immediately stale
    gcTime: 0, // No garbage collection time - immediate cleanup
    refetchOnMount: true, // Always refetch on component mount
    refetchOnWindowFocus: false // Don't refetch on window focus to avoid excessive requests
  })
}

export const usePersonDetails = (id: number | null) => {
  return useQuery({
    queryKey: [CONTACTS_QUERY_KEY, 'person', id],
    queryFn: async (): Promise<Person> => {
      if (!id) throw new Error('Person ID is required')
      const response = await apiInstance.get<Person>(`/admin/contacts/persons/${id}/`)
      return response.data
    },
    enabled: !!id,
    // Security: Disable caching for sensitive personal data
    staleTime: 0, // Data is immediately stale
    gcTime: 0, // No garbage collection time - immediate cleanup
    refetchOnMount: true, // Always refetch on component mount
    refetchOnWindowFocus: false // Don't refetch on window focus to avoid excessive requests
  })
}

export const usePersonUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PersonUpdateData }): Promise<Person> => {
      const response = await apiInstance.put<Person>(`/admin/contacts/persons/${id}/`, data)
      return response.data
    },
    onSuccess: (updatedPerson) => {
      // Security: Don't cache updated person data - invalidate all related queries
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_QUERY_KEY, 'person', updatedPerson.id]
      })

      // Invalidate persons list to refresh search results
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_QUERY_KEY, 'persons']
      })
    }
  })
}

export const usePersonPartialUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PersonUpdateData }): Promise<Person> => {
      const response = await apiInstance.patch<Person>(`/admin/contacts/persons/${id}/`, data)
      return response.data
    },
    onSuccess: (updatedPerson) => {
      // Security: Don't cache updated person data - invalidate all related queries
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_QUERY_KEY, 'person', updatedPerson.id]
      })

      // Invalidate persons list to refresh search results
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_QUERY_KEY, 'persons']
      })

      // Invalidate bank account data to refresh BankAccountConfirmation component
      queryClient.invalidateQueries({
        queryKey: [CONTACTS_QUERY_KEY, 'bank-account', updatedPerson.id]
      })
    }
  })
}

export const usePersonBankAccount = (id: number | null) => {
  const { accessToken } = useAccessToken()

  return useQuery({
    queryKey: [CONTACTS_QUERY_KEY, 'bank-account', id],
    queryFn: async (): Promise<PersonBankAccount> => {
      if (!id) throw new Error('Person ID is required')
      const response = await apiInstance.get<PersonBankAccount>(
        `/admin/contacts/persons/${id}/bank-account/`,
        {
          headers: {
            authorization: `Bearer ${accessToken}`
          }
        }
      )
      return response.data
    },
    enabled: !!id && !!accessToken,
    // Security: Disable caching for sensitive financial data
    staleTime: 0, // Data is immediately stale
    gcTime: 0, // No garbage collection time - immediate cleanup
    refetchOnMount: true, // Always refetch on component mount
    refetchOnWindowFocus: false // Don't refetch on window focus to avoid excessive requests
  })
}
